/**
 * Achievement / Badge definitions for DUEL.
 * Iron. Steel. Chalk. Earned, not given.
 */

import { supabase } from './supabase';

export interface AchievementDef {
  id: string;
  name: string;
  description: string;
  /** Icon name from lucide-react-native */
  icon: string;
  /** Tier for visual styling */
  tier: 'bronze' | 'silver' | 'gold' | 'diamond';
}

export interface AchievementState extends AchievementDef {
  unlocked: boolean;
  unlockedAt?: string;
}

export const ACHIEVEMENT_DEFS: AchievementDef[] = [
  {
    id: 'iron_starter',
    name: 'Iron Starter',
    description: 'Log your first workout',
    icon: 'Dumbbell',
    tier: 'bronze',
  },
  {
    id: 'century_club',
    name: 'Century Club',
    description: 'Complete 100 workouts',
    icon: 'Target',
    tier: 'gold',
  },
  {
    id: 'duel_victor',
    name: 'Duel Victor',
    description: 'Win your first duel',
    icon: 'Swords',
    tier: 'bronze',
  },
  {
    id: 'streak_lord',
    name: 'Streak Lord',
    description: 'Maintain a 30-day workout streak',
    icon: 'Flame',
    tier: 'gold',
  },
  {
    id: 'pr_machine',
    name: 'PR Machine',
    description: 'Set 10 personal records',
    icon: 'TrendingUp',
    tier: 'silver',
  },
  {
    id: 'diamond_rank',
    name: 'Diamond Rank',
    description: 'Reach Diamond division',
    icon: 'Gem',
    tier: 'diamond',
  },
  {
    id: 'social_butterfly',
    name: 'Social Butterfly',
    description: 'Share 5 workouts',
    icon: 'Share2',
    tier: 'bronze',
  },
  {
    id: 'iron_will',
    name: 'Iron Will',
    description: 'Complete all 5 Core 5 lifts',
    icon: 'Shield',
    tier: 'silver',
  },
];

/**
 * Evaluate which achievements are unlocked for a given user.
 * Returns all achievement definitions with their unlock state.
 */
export async function evaluateAchievements(userId: string): Promise<AchievementState[]> {
  // Fetch data in parallel
  const [workoutRes, duelRes, prRes, profileRes, sharesRes] = await Promise.all([
    supabase
      .from('workouts')
      .select('performed_at', { count: 'exact' })
      .eq('user_id', userId)
      .order('performed_at', { ascending: true })
      .limit(500),
    supabase
      .from('duels')
      .select('id', { count: 'exact', head: true })
      .eq('winner_user_id', userId)
      .in('status', ['completed', 'resolved']),
    supabase
      .from('strength_events')
      .select('id', { count: 'exact', head: true })
      .eq('profile_id', userId),
    supabase
      .from('profiles')
      .select('duel_standard_components, current_rank, duel_standard_strength_kg')
      .eq('id', userId)
      .maybeSingle(),
    supabase
      .from('posts')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', userId)
      .not('media_url', 'is', null),
  ]);

  const workoutCount = workoutRes.count ?? 0;
  const workoutDates = (workoutRes.data ?? []).map(w => w.performed_at);
  const wins = duelRes.count ?? 0;
  const prCount = prRes.count ?? 0;
  const profile = profileRes.data;
  const sharesCount = sharesRes.count ?? 0;

  // Calculate streak
  let maxStreak = 0;
  if (workoutDates.length > 0) {
    const uniqueDays = [...new Set(workoutDates.map(d => new Date(d).toISOString().slice(0, 10)))].sort();
    let streak = 1;
    for (let i = 1; i < uniqueDays.length; i++) {
      const prev = new Date(uniqueDays[i - 1]);
      const curr = new Date(uniqueDays[i]);
      const diff = (curr.getTime() - prev.getTime()) / (1000 * 60 * 60 * 24);
      if (diff <= 1) {
        streak++;
        maxStreak = Math.max(maxStreak, streak);
      } else {
        streak = 1;
      }
    }
    maxStreak = Math.max(maxStreak, streak);
  }

  // Check baseline completion
  let baselineComplete = false;
  if (profile?.duel_standard_components) {
    let comp = profile.duel_standard_components;
    try { if (typeof comp === 'string') comp = JSON.parse(comp); } catch (e) { console.warn('Failed to parse duel_standard_components:', e); comp = null; }
    if (comp) {
        // V1 lift pool: bench, squat, deadlift, weighted_pull_up, dead_hang (farmers_carry removed)
      const v1Keys = ['bench', 'squat', 'deadlift', 'weighted_pull_up', 'dead_hang'];
      const legacyKeys = ['bench', 'squat', 'deadlift', 'weighted_pull_up', 'farmers_carry'];
      baselineComplete = v1Keys.every(k => !!(comp as any)[k]) 
        || legacyKeys.every(k => !!(comp as any)[k])
        || (profile?.duel_standard_strength_kg ?? 0) > 0; // if score exists, baseline is done
    }
  }

  // Check rank for diamond
  const isDiamond = typeof profile?.current_rank === 'number' && profile.current_rank <= 10;



  const unlockMap: Record<string, { unlocked: boolean }> = {
    iron_starter: { unlocked: workoutCount >= 1 },
    century_club: { unlocked: workoutCount >= 100 },
    duel_victor: { unlocked: wins >= 1 },
    streak_lord: { unlocked: maxStreak >= 30 },
    pr_machine: { unlocked: prCount >= 10 },
    diamond_rank: { unlocked: isDiamond },
    social_butterfly: { unlocked: sharesCount >= 5 },
    iron_will: { unlocked: baselineComplete },
  };

  return ACHIEVEMENT_DEFS.map(def => ({
    ...def,
    unlocked: unlockMap[def.id]?.unlocked ?? false,
  }));
}
